import _ from "lodash";
import { Location, matchPath } from "react-router-dom";
import { AvisConstants } from "../data/avisConstants";
import { AvisRoute, avisRoutes } from "../data/avisRoutes";
import { PortalReportStatusTypeInfo } from "../generated/data-contracts";
import { IPortalKind, PortalType } from "../models";
import {
  IEditableRowState,
  IRowState,
} from "../components/shared/components/editableList";

export class AvisUtils {
  static getPortalKind(): IPortalKind {
    const pathname = location.pathname.slice(1);
    const pathParts = pathname.split("/");

    const portalTypeParsed = pathParts[0] as PortalType;
    switch (portalTypeParsed) {
      case "authors":
        return {
          portalType: portalTypeParsed,
          rightsHoldersEnabled: true,
          requirePerimissions: "isAuthor",
        };
      case "licensees":
        return {
          portalType: portalTypeParsed,
          requirePerimissions: "isLicenseTaker",
        };
      case "blank-media":
        return {
          portalType: portalTypeParsed,
          requirePerimissions: "isBlankMediaUser",
        };
      case "common":
        return {
          portalType: "common",
          requirePerimissions: undefined,
        };
    }
  }

  static readonly getRouteByUrl = (url?: string): AvisRoute | undefined => {
    if (!url) return;

    return avisRoutes.find((x) => matchPath({ path: x.path }, url) !== null);
  };
  static getStartPagePath(): string {
    const portalKind = AvisUtils.getPortalKind();
    return `/${portalKind?.portalType}`;
  }

  static sessionDataCleanup() {
    const cleanFilterDataKeys = [
      AvisConstants.creations_filter_values_key,
      AvisConstants.licenses_filter_values_key,
      AvisConstants.licenses_child_filter_values_key,
      AvisConstants.creations_search_filter_values_key,
      AvisConstants.licenses_creations_filter_values_key,
      AvisConstants.licenses_reports_active_filter_values_key,
      AvisConstants.blank_media_applications_filter_values_key,
    ];
    cleanFilterDataKeys.forEach((cleanKey) => {
      AvisUtils.sessionDataCleanupFilterData(cleanKey);
    });

    sessionStorage.removeItem(AvisConstants.creations_filter_classes_key);
    sessionStorage.removeItem(AvisConstants.creations_filter_statuses_key);
    sessionStorage.removeItem(
      AvisConstants.licenses_creations_filter_classes_key,
    );
    sessionStorage.removeItem(
      AvisConstants.creations_filter_statuses_unidentfied_key,
    );

    sessionStorage.removeItem(AvisConstants.user_storage_key);
  }
  static sessionDataCleanupFilterData(forKey: string) {
    sessionStorage.removeItem(`${forKey}${AvisConstants.grid_filter_suffix}`);
    sessionStorage.removeItem(`${forKey}${AvisConstants.grid_data_suffix}`);
  }

  static isKnownLocation(location: Location<any>): boolean {
    return this.getRouteByUrl(location.pathname) !== undefined;
  }

  static readonly updateLastActivityTime = () => {
    const date = new Date();
    const timestamp = date.getTime();
    localStorage.setItem(
      AvisConstants.user_last_activity_time_key,
      timestamp.toString(),
    );
  };
  static atobParam(paramName: string, value: string): string {
    try {
      return atob(value);
    } catch (e) {
      throw new Error(
        `Failed to decode "${paramName}" value "${value}"! Error: ${e}`,
      );
    }
  }

  static allItemsHaveProp(
    array: any[] | undefined,
    propertyName: string,
  ): boolean {
    if (!array) return true;
    if (array.length === 0) return true;

    return array.every((obj) =>
      Object.prototype.hasOwnProperty.call(obj, propertyName),
    );
  }
  static hasDeletedRows(
    arrayInit: any[] | undefined,
    arrayToCompare: any[] | undefined,
    propertyName: string,
  ): boolean {
    if (!arrayInit) return false;
    if (arrayInit.length === 0) return false;
    if (!arrayToCompare) return true;
    if (arrayToCompare.length === 0) return true;

    const keysInit = arrayInit.map((item) => item?.[propertyName]);
    const keysToCompare = arrayToCompare.map((item) => item?.[propertyName]);

    const result = !keysInit.every(
      (initKey, index) => initKey === keysToCompare[index],
    );

    return result;
  }

  static isEditablePortalReportStatusTypeInfo(
    status?: PortalReportStatusTypeInfo,
  ) {
    return status?.value === "NotSubmitted" || status?.value === "Rejected";
  }

  static canGoHistoryBack() {
    return history.length > 2 && history?.state?.key;
  }
}

type RecursivelyReplaceNullWithUndefined<T> = T extends null
  ? undefined
  : T extends Date
    ? T
    : {
        [K in keyof T]: T[K] extends (infer U)[]
          ? RecursivelyReplaceNullWithUndefined<U>[]
          : RecursivelyReplaceNullWithUndefined<T[K]>;
      };

export function prepareCollectionForPost<T, P extends IEditableRowState>(
  obj: T,
  collection: keyof T,
) {
  if (!obj) return;
  if (!obj[collection]) return;

  //Prepare collection for posting
  //1) Remove deleted rows
  //2) remove rowState & initialRowValue props from payload
  obj[collection] = (obj[collection] as IRowState<P>[])
    ?.filter((item) => item.rowState !== "Delete")
    .map((item) => excludeProps(item, "rowState", "initialRowValue")) as any;
}

export function indexArrayByKey<T>(array: T[], key: keyof T): Map<string, T> {
  return array.reduce(
    (map, obj) => map.set((obj as any)[key.toString()], obj),
    new Map<string, T>(),
  );
}

export function nullsToUndefined<T>(
  obj: T,
): RecursivelyReplaceNullWithUndefined<T> {
  if (obj === undefined) return obj as any;
  if (obj === null) {
    return undefined as any;
  }

  // object check based on: https://stackoverflow.com/a/51458052/6489012
  if (obj.constructor.name === "Object") {
    for (const key in obj) {
      obj[key] = nullsToUndefined(obj[key]) as any;
    }
  }
  if (obj.constructor.name === "Array") {
    const array = obj as any as [];
    const arrayNew = array.map((item) => nullsToUndefined(item));
    return arrayNew as any;
  }
  return obj as any;
}

export function excludeProps<T extends object = { [key: string]: any }>(
  o: T,
  ...args: (keyof T)[]
): Partial<T> {
  const clone = { ...(o as object) } as any;

  args.forEach((prop) => delete clone[prop]);

  if (clone["dispatch"]) delete clone["dispatch"];

  if (clone["global"]) delete clone["global"];

  return clone;
}

export const generateInitRowKeys = (array: Array<any>) => {
  const arrayOfKeys: string[] = [];

  array.forEach((element) => {
    if (element) {
      arrayOfKeys.push(_.uniqueId());
    }
  });

  return arrayOfKeys;
};
